import type { Article } from '../types';
import { h2, p, ul, ol, callout, table } from '../b';

const article: Article = {
  slug: 'how-to-accept-telegram-stars-payments',
  title: 'How to Accept Telegram Stars Payments: Step-by-Step Guide for Creators and Merchants',
  metaTitle: 'How to Accept Telegram Stars Payments (Step-by-Step)',
  metaDescription:
    'Learn how to accept Telegram Stars payments in minutes: with a no-code payment link, through your own bot, or in a Mini App. Fees, payouts to TON and best practices.',
  keywords: [
    'accept telegram stars payments',
    'how to accept telegram stars',
    'telegram stars payment',
    'telegram stars payments',
    'stars payment telegram',
    'receive telegram stars',
    'telegram stars for business',
    'telegram stars merchant',
  ],
  category: 'Guides',
  datePublished: '2026-09-05',
  dateModified: '2026-10-15',
  readingTimeMin: 8,
  excerpt:
    'There are three ways to accept Telegram Stars: a no-code payment link, your own Telegram bot, or a Mini App. This guide walks through each option, explains the fees and the 21-day payout rule, and shows how to get your first Stars today.',
  keyTakeaways: [
    'The quickest way to accept Telegram Stars is a payment link created on a Stars payment platform: no bot, no server, no code.',
    'Developers can accept Stars directly with the Bot API using currency XTR; invoices can be sent in chat or shared as links.',
    'Stars you receive become withdrawable after 21 days and can be paid out in TON; Telegram Stars Payment Platform charges a flat 3% and has a 100 Stars minimum payout.',
    'Stars are mandatory for selling digital goods inside Telegram on iOS and Android, so most creator and bot businesses should offer them.',
  ],
  body: [
    h2('Who can accept Telegram Stars?'),
    p(
      'Anyone with a Telegram account can receive Stars. You do not need a company, a merchant account or a bank integration. Stars are designed for **digital goods and services**: subscriptions, courses, software, premium content, memberships, consulting, in-app items and tips. If you sell physical products you can still use Stars, but keep in mind that payouts arrive in TON rather than in your bank account.',
    ),
    p('There are three practical ways to accept Stars, from the simplest to the most flexible.'),

    h2('Option 1: Accept Stars with a payment link (no code)'),
    p(
      'A payment link is a URL that opens a ready-to-pay Stars invoice in Telegram. You create it once, share it anywhere (channel post, bio, website, DM) and every payment lands in your balance. This is what **Telegram Stars Payment Platform** provides.',
    ),
    ol([
      '[Create a free account](/login). You log in with Telegram, there is no credit card and no monthly fee.',
      'Open **Invoices → New** in the dashboard. Enter a title (for example "Premium Access"), a short description, and the amount in Stars.',
      'Click **Create**. You get a payment link that you can copy, plus an optional short redirect link (`telegram-stars.io/r/your-slug`) with click tracking.',
      'Share the link. Your customer taps it, sees the native Telegram payment sheet, and confirms with their Stars balance (or buys Stars on the spot).',
      'The payment is credited to your balance instantly, with a full ledger entry. After the 21-day holding period you can withdraw to any TON wallet.',
    ]),
    callout(
      'Tip: create one link per product or price point. You can see which link converts best directly in the dashboard, and disable a link at any time.',
    ),
    p('For a deeper look at links, redirects and tracking, read [Telegram Stars payment links explained](/blog/telegram-stars-payment-links).'),

    h2('Option 2: Accept Stars through your own Telegram bot'),
    p(
      'If you already run a bot, you can sell directly from it. In the Bot API, Stars are a regular payment currency with the code `XTR`, except that you do not need a payment provider token.',
    ),
    ol([
      'Call `sendInvoice` (sends the invoice as a message) or `createInvoiceLink` (returns a shareable URL) with `currency="XTR"`, an empty `provider_token`, and a single price item whose `amount` is the number of Stars.',
      'When the user confirms, Telegram sends your bot a `pre_checkout_query`. Answer it with `answerPreCheckoutQuery(ok=true)` within 10 seconds, or the payment fails.',
      'Telegram then sends a message containing `successful_payment`, with the `telegram_payment_charge_id`. Store it: you need it for refunds and reconciliation.',
      'Deliver the product (unlock the feature, send the file, add the user to a group).',
    ]),
    p(
      'This gives you full control but also full responsibility: hosting the bot, handling retries, building a ledger, and managing withdrawals on Fragment. We cover the implementation in detail in [Telegram Stars Bot API guide](/blog/telegram-stars-bot-api-developer-guide).',
    ),

    h2('Option 3: Accept Stars in a Mini App'),
    p(
      'Telegram Mini Apps (web apps that run inside Telegram) can trigger Stars payments too. Your backend creates an invoice link with `createInvoiceLink`, and the Mini App opens it with `WebApp.openInvoice(url, callback)`. The payment sheet appears over your app and the callback tells you whether the user paid. This is how most Telegram games sell in-game items.',
    ),

    h2('Which option should you choose?'),
    table(
      ['', 'Payment link (platform)', 'Own bot', 'Mini App'],
      [
        ['Setup time', 'Minutes', 'Hours to days', 'Days'],
        ['Coding required', 'No', 'Yes', 'Yes'],
        ['Hosting required', 'No', 'Yes', 'Yes'],
        ['Ledger and dashboard', 'Included', 'Build it yourself', 'Build it yourself'],
        ['Withdrawals', 'From the dashboard, min. 100 Stars', 'Fragment, min. 1,000 Stars', 'Fragment, min. 1,000 Stars'],
        ['Best for', 'Creators, coaches, communities, merchants', 'Developers with an existing bot', 'Games and web apps'],
      ],
    ),

    h2('Fees when accepting Telegram Stars'),
    p(
      'Telegram itself does not charge sellers a per-transaction fee for receiving Stars. The costs sit elsewhere: the buyer pays a store commission when purchasing Stars, and the seller realises a conversion rate when turning Stars into TON. If you use a payment platform, add its fee. Telegram Stars Payment Platform charges a **flat 3%** with no setup or monthly fees. See [Telegram Stars fees explained](/blog/telegram-stars-fees-explained) for a worked example.',
    ),

    h2('The 21-day holding period'),
    p(
      'Stars you receive are not withdrawable immediately. Telegram applies a 21-day hold to cover refunds and disputes. Telegram Stars Payment Platform mirrors this rule and shows you a "maturing" balance with the exact date each payment becomes available, so you always know what you can withdraw. Details in [Refunds, the 21-day hold and payout rules](/blog/telegram-stars-refunds-21-day-hold).',
    ),

    h2('Best practices to sell more with Stars'),
    ul([
      '**Price in round Stars amounts** (50, 100, 250, 500). They map neatly to the Stars packs users buy.',
      '**Explain what the buyer gets** in the invoice description. The payment sheet shows it and it reduces refund requests.',
      '**Pin your payment link** in your channel or group and put it in your bio.',
      '**Offer a subscription** for recurring content instead of one-off posts; Stars subscriptions renew automatically.',
      '**Use short redirect links** for tracking campaigns, so you know which post or ad generated the sale.',
      '**Deliver instantly**. Stars buyers expect the same immediacy as an in-app purchase.',
    ]),

    h2('Getting your first Stars payment today'),
    p(
      'If you want to be live in the next ten minutes: [sign in with Telegram](/login), create an invoice link for your best-selling product, post it to your audience, and watch the balance update in real time. There is nothing to install and the 3% fee only applies when you actually get paid.',
    ),
  ],
  faq: [
    {
      q: 'Do I need a Telegram bot to accept Stars?',
      a: 'Not if you use a payment platform. Telegram Stars Payment Platform creates the invoices for you and provides a shareable payment link. You only need your own bot if you want to build a custom flow.',
    },
    {
      q: 'Can I accept Telegram Stars without a company?',
      a: 'Yes. Any individual with a Telegram account can receive Stars. Check your local tax rules for declaring the income.',
    },
    {
      q: 'How long before I can withdraw the Stars I receive?',
      a: 'Stars become withdrawable 21 days after the payment. Telegram Stars Payment Platform displays the availability date of every payment in your ledger.',
    },
    {
      q: 'What is the minimum payout?',
      a: 'On Telegram Stars Payment Platform the minimum withdrawal is 100 Stars, paid to your TON wallet. Withdrawing directly on Fragment requires 1,000 Stars.',
    },
    {
      q: 'Can customers pay in Stars if they do not have any?',
      a: 'Yes. When a user without enough Stars opens your invoice, Telegram offers to buy Stars on the spot, then completes the payment.',
    },
  ],
  relatedSlugs: [
    'telegram-stars-payment',
    'telegram-stars-subscriptions',
    'telegram-stars-payment-links',
    'withdraw-telegram-stars-to-ton',
  ],
};

export default article;
